// Format helpers ported from sales-ops web/src/lib/format.ts.
// Pure functions — no DOM, no Node — usable anywhere in the frontend.

const STATUS_ALIAS: Record<string, string> = {
  evaluada: "EVALUATED",
  evaluado: "EVALUATED",
  condicional: "EVALUATED",
  hold: "EVALUATED",
  evaluar: "EVALUATED",
  verificar: "EVALUATED",
  aplicada: "APPLIED",
  aplicado: "APPLIED",
  enviada: "APPLIED",
  sent: "APPLIED",
  respondida: "RESPONDED",
  respondido: "RESPONDED",
  contestada: "RESPONDED",
  entrevista: "INTERVIEW",
  oferta: "OFFER",
  rechazada: "REJECTED",
  rechazado: "REJECTED",
  descartada: "DISCARDED",
  descartado: "DISCARDED",
  cerrada: "DISCARDED",
  cancelada: "DISCARDED",
  duplicado: "DISCARDED",
  repost: "DISCARDED",
  monitor: "SKIP",
  no_aplicar: "SKIP",
  "no aplicar": "SKIP",
};

export const CANONICAL_STATES = [
  "Lead", "Qualified", "Discovery", "Proposal", "Demo",
  "Negotiation", "Won", "Lost", "Passed", "Watch",
] as const;

/** Normalize a status string to its canonical uppercase form. */
export function canonStatus(s: string): string {
  const k = s.trim().toLowerCase();
  if (k === "" || k === "\u2014" || k === "-") return "DISCARDED";
  return STATUS_ALIAS[k] ?? s.toUpperCase();
}

/**
 * Tailwind dot colour for a status, mirroring the Go TUI:
 * green for interview/offer, sky for applied/responded,
 * red for skip/rejected, gray for discarded, neutral for evaluated.
 */
export function statusDot(status: string): string {
  const c = canonStatus(status);
  if (c.includes("INTERVIEW") || c.includes("OFFER")) return "bg-emerald-400";
  if (c.includes("APPLIED") || c.includes("RESPONDED")) return "bg-sky-400";
  if (c.includes("REJECTED") || c.includes("SKIP")) return "bg-red-400";
  if (c.includes("DISCARDED")) return "bg-zinc-600";
  return "bg-zinc-500";
}

/** Extract VERDICT score from evaluation output. */
export function parseVerdictScore(text: string): number | undefined {
  const match = text.match(/VERDICT:\s*(\d+(?:\.\d+)?)\s*\/\s*5/i);
  if (!match) return undefined;
  const score = parseFloat(match[1]);
  return Number.isFinite(score) ? score : undefined;
}

export function extractVerdictLine(text: string): string | undefined {
  const match = text.match(/VERDICT:\s*.+$/im);
  return match?.[0]?.trim();
}

/** Resolve a display score from job fields or embedded evaluation text. */
export function resolveJobScore(job: {
  score?: number;
  fitScore?: number;
  evaluation?: string;
}): number | undefined {
  if (typeof job.score === "number" && !Number.isNaN(job.score)) return job.score;
  if (typeof job.fitScore === "number" && !Number.isNaN(job.fitScore)) return job.fitScore;
  if (job.evaluation) {
    const parsed = parseVerdictScore(job.evaluation);
    if (parsed !== undefined) return parsed;
  }
  return undefined;
}

export function scoreNum(s: string): number {
  const m = s.match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : NaN;
}

/**
 * Score tone, mirroring Go TUI thresholds:
 * >= 4.2 green, >= 3.8 yellow, >= 3.0 muted, < 3.0 bad.
 */
export function scoreTone(
  score: string,
): "good" | "warn" | "bad" | "muted" {
  const num = scoreNum(score);
  if (!Number.isNaN(num)) {
    if (num >= 4.2) return "good";
    if (num >= 3.8) return "warn";
    if (num >= 3.0) return "muted";
    return "bad";
  }
  const g = score.trim().toUpperCase()[0];
  if (g === "A") return "good";
  if (g === "B") return "warn";
  if (g === "C") return "muted";
  if (g === "D" || g === "E" || g === "F") return "bad";
  return "muted";
}

/** Block-G legitimacy tier → tone. */
export function legitimacyTone(
  l: string,
): "good" | "warn" | "bad" | "muted" {
  const s = l.toLowerCase();
  if (s.includes("high") || s.includes("confian") || s.includes("legit"))
    return "good";
  if (s.includes("caution") || s.includes("precau") || s.includes("caut"))
    return "warn";
  if (
    s.includes("suspic") ||
    s.includes("sospech") ||
    s.includes("scam") ||
    s.includes("fake")
  )
    return "bad";
  return "muted";
}